import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Drawer,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Typography,
  notification,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, EyeOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { UpdateRelease } from '../api/go';
import type {
  UpdateManifest,
  UpdateManifestModule,
  UpdateModuleStatus,
  UpdateModuleVersionRecord,
  UpdateReleaseDetailResp,
  UpdateReleaseRecord,
  UpdateReleaseStatus,
} from '../api/go';
import {
  RELEASE_STATUS_ARCHIVED,
  RELEASE_STATUS_BASELINE,
  buildReleaseStatusSwitchOptions,
  canManageModuleRollout,
  getErrorMessage,
  moduleStatusMap,
  normalizeManifest,
  nullableText,
  parseJSONErrorMessage,
  releaseStatusMap,
  releaseStatusOptions,
  releaseStatusText,
  renderMultilineText,
  renderOssKey,
  renderStatusTag,
  showCheckJSONPublishWarning,
} from './shared';
import { useRolloutEditor } from './rollout_editor';

type ReleaseFilterForm = {
  name?: string;
  build_number?: string;
  status?: UpdateReleaseStatus;
};

type ReleaseUploadForm = {
  name: string;
  remark?: string;
  update_description?: string;
};

type ReleaseEditForm = {
  name: string;
  remark?: string;
  update_description?: string;
};

export function ReleaseTab(props: { onReleaseChanged: () => void; onRolloutChanged: () => void }) {
  const { onReleaseChanged, onRolloutChanged } = props;
  const [filterForm] = Form.useForm<ReleaseFilterForm>();
  const [uploadForm] = Form.useForm<ReleaseUploadForm>();
  const [editForm] = Form.useForm<ReleaseEditForm>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [manifest, setManifest] = useState<UpdateManifest | null>(null);
  const [manifestFileName, setManifestFileName] = useState('');
  const [manifestParseError, setManifestParseError] = useState('');
  const [records, setRecords] = useState<UpdateReleaseRecord[]>([]);
  const [page, setPage] = useState({ current: 1, size: 20, total: 0 });
  const [listLoading, setListLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<UpdateReleaseDetailResp | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UpdateReleaseRecord | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [switchingReleaseID, setSwitchingReleaseID] = useState<number | null>(null);
  const [expandedDetails, setExpandedDetails] = useState<Record<number, UpdateReleaseDetailResp>>({});
  const [expandedLoadingIDs, setExpandedLoadingIDs] = useState<Record<number, boolean>>({});
  const rolloutEditor = useRolloutEditor({
    onSaved: async (target) => {
      await refreshReleaseDetail(target.release_id);
      onRolloutChanged();
    },
  });

  async function loadReleaseList(current = page.current, size = page.size) {
    setListLoading(true);
    try {
      const values = filterForm.getFieldsValue();
      const data = await UpdateRelease.list({
        name: values.name?.trim() || undefined,
        build_number: values.build_number?.trim() || undefined,
        status: values.status,
        current,
        size,
      });
      setRecords(data.records || []);
      setPage({ current: data.current, size: data.size, total: data.total });
    } catch (err) {
      notification.error({ message: '加载 Release 失败', description: await getErrorMessage(err) });
    } finally {
      setListLoading(false);
    }
  }

  useEffect(() => {
    loadReleaseList(1, 20);
  }, []);

  async function refreshReleaseDetail(releaseID: number) {
    const data = await UpdateRelease.detail(releaseID);
    setExpandedDetails((prev) => ({ ...prev, [releaseID]: data }));
    setDetail(data);
    return data;
  }

  async function loadExpandedReleaseDetail(row: UpdateReleaseRecord, force = true) {
    if (!force && expandedDetails[row.id]) {
      return;
    }
    setExpandedLoadingIDs((prev) => ({ ...prev, [row.id]: true }));
    try {
      await refreshReleaseDetail(row.id);
    } catch (err) {
      notification.error({ message: '加载 Release 模块失败', description: await getErrorMessage(err) });
    } finally {
      setExpandedLoadingIDs((prev) => ({ ...prev, [row.id]: false }));
    }
  }

  async function handleManifestFile(file: File) {
    setManifest(null);
    setManifestFileName(file.name);
    setManifestParseError('');

    if (!file.name.toLowerCase().endsWith('.json')) {
      setManifestParseError('请选择 .json 文件');
      return;
    }

    const raw = await file.text();
    try {
      setManifest(normalizeManifest(JSON.parse(raw)));
    } catch (err) {
      setManifestParseError(parseJSONErrorMessage(err, raw));
    }
  }

  async function uploadRelease(values: ReleaseUploadForm) {
    if (!manifest) {
      notification.warning({ message: '请先选择并解析 manifest JSON' });
      return;
    }

    setUploading(true);
    try {
      const resp = await UpdateRelease.upload({
        name: values.name.trim(),
        remark: values.remark?.trim(),
        update_description: values.update_description?.trim(),
        manifest,
      });
      if (resp.duplicate) {
        notification.warning({ message: 'manifest 已存在', description: `已存在 Release: ${resp.release.name}` });
      } else {
        notification.success({ message: '上传成功', description: `已创建 ${resp.modules.length} 个模块版本` });
        uploadForm.resetFields();
        setManifest(null);
        setManifestFileName('');
      }
      await loadReleaseList(1, page.size);
      onReleaseChanged();
    } catch (err) {
      notification.error({ message: '上传 Release 失败', description: await getErrorMessage(err) });
    } finally {
      setUploading(false);
    }
  }

  async function openDetail(row: UpdateReleaseRecord) {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      await refreshReleaseDetail(row.id);
    } catch (err) {
      notification.error({ message: '加载详情失败', description: await getErrorMessage(err) });
    } finally {
      setDetailLoading(false);
    }
  }

  function openEdit(row: UpdateReleaseRecord) {
    setEditTarget(row);
    editForm.setFieldsValue({
      name: row.name,
      remark: row.remark,
      update_description: row.update_description,
    });
    setEditOpen(true);
  }

  async function submitEdit(values: ReleaseEditForm) {
    if (!editTarget) {
      return;
    }

    setEditLoading(true);
    try {
      const resp = await UpdateRelease.update({
        id: editTarget.id,
        name: values.name?.trim(),
        remark: values.remark?.trim(),
        update_description: values.update_description?.trim(),
      });
      notification.success({ message: '更新成功' });
      showCheckJSONPublishWarning(resp.check_json_publish);
      setEditOpen(false);
      setEditTarget(null);
      await loadReleaseList();
      await refreshReleaseDetail(editTarget.id).catch(() => undefined);
      onReleaseChanged();
    } catch (err) {
      notification.error({ message: '更新 Release 失败', description: await getErrorMessage(err) });
    } finally {
      setEditLoading(false);
    }
  }

  async function doSwitchReleaseStatus(row: UpdateReleaseRecord, status: UpdateReleaseStatus, createRolloutDrafts = false) {
    setSwitchingReleaseID(row.id);
    try {
      const resp = await UpdateRelease.switchStatus({ id: row.id, status, create_rollout_drafts: createRolloutDrafts });
      const archivedText = resp.archived_baseline_release_ids?.length
        ? `，已自动归档旧基线 Release: ${resp.archived_baseline_release_ids.join(', ')}`
        : '';
      const draftText = resp.created_rollout_count ? `，已创建 ${resp.created_rollout_count} 个 rollout 草稿` : '';
      notification.success({
        message: '状态切换成功',
        description: `${row.name} 已切换为${releaseStatusText(status)}${archivedText}${draftText}`,
      });
      showCheckJSONPublishWarning(resp.check_json_publish);
      await loadReleaseList();
      await refreshReleaseDetail(row.id).catch(() => undefined);
      onReleaseChanged();
    } catch (err) {
      notification.error({ message: '状态切换失败', description: await getErrorMessage(err) });
    } finally {
      setSwitchingReleaseID(null);
    }
  }

  function switchReleaseStatus(row: UpdateReleaseRecord, status: UpdateReleaseStatus) {
    if (status === RELEASE_STATUS_BASELINE) {
      let createRolloutDrafts = false;
      Modal.confirm({
        title: '确认设置基线版本',
        content: (
          <Space direction="vertical">
            <Typography.Text>设置新的基线版本后，旧基线版本会自动归档。</Typography.Text>
            <Checkbox onChange={(event) => (createRolloutDrafts = event.target.checked)}>为所有模块创建 rollout 草稿</Checkbox>
          </Space>
        ),
        okText: '确认设置',
        cancelText: '取消',
        onOk: () => doSwitchReleaseStatus(row, status, createRolloutDrafts),
      });
      return;
    }
    if (status === RELEASE_STATUS_ARCHIVED) {
      Modal.confirm({
        title: '确认归档 Release',
        content: '归档后该 Release 不能再切换状态，也不能用于新建 rollout。',
        okText: '确认归档',
        cancelText: '取消',
        onOk: () => doSwitchReleaseStatus(row, status),
      });
      return;
    }
    doSwitchReleaseStatus(row, status);
  }

  const manifestColumns: ColumnsType<UpdateManifestModule> = [
    { title: '模块 Key', dataIndex: 'key', width: 160 },
    { title: '版本号', dataIndex: 'version', width: 140 },
    {
      title: 'OSS Key',
      dataIndex: 'url',
      ellipsis: true,
      render: (value: string) => renderOssKey(value),
    },
    {
      title: '校验串',
      dataIndex: 'sha1',
      width: 260,
      render: (value: string) => <Typography.Text copyable={{ text: value }}>{value}</Typography.Text>,
    },
  ];

  const releaseColumns: ColumnsType<UpdateReleaseRecord> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '版本名称',
      dataIndex: 'name',
      width: 260,
      render: (value: string) => <Typography.Text copyable={{ text: value }}>{value}</Typography.Text>,
    },
    { title: '协议版本', dataIndex: 'quick_version', width: 100 },
    { title: '构建号', dataIndex: 'build_number', width: 150 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: UpdateReleaseStatus) => renderStatusTag(value, releaseStatusMap),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 180,
      ellipsis: true,
      render: nullableText,
    },
    { title: '更新时间', dataIndex: 'updated_at', width: 180 },
    {
      title: '操作',
      width: 300,
      fixed: 'right',
      render: (_, row) => {
        return (
          <Space wrap>
            <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(row)}>
              详情
            </Button>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
              修改
            </Button>
            <Select
              size="small"
              value={row.status}
              style={{ width: 136 }}
              options={buildReleaseStatusSwitchOptions(row.status)}
              loading={switchingReleaseID === row.id}
              disabled={switchingReleaseID === row.id}
              popupMatchSelectWidth={false}
              onChange={(status) => switchReleaseStatus(row, status)}
            />
          </Space>
        );
      },
    },
  ];

  const releaseModuleColumns: ColumnsType<UpdateModuleVersionRecord> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '模块 Key', dataIndex: 'module_key', width: 160 },
    { title: '版本号', dataIndex: 'module_version', width: 140 },
    { title: '构建号', dataIndex: 'build_number', width: 150 },
    {
      title: '模块状态',
      dataIndex: 'status',
      width: 110,
      render: (value: UpdateModuleStatus) => renderStatusTag(value, moduleStatusMap),
    },
    {
      title: 'OSS Key',
      dataIndex: 'url',
      ellipsis: true,
      render: (value: string, row) => renderOssKey(value, row.download_url),
    },
    {
      title: '校验串',
      dataIndex: 'sha1',
      width: 260,
      render: (value: string) => <Typography.Text copyable={{ text: value }}>{value}</Typography.Text>,
    },
    {
      title: '操作',
      width: 130,
      fixed: 'right',
      render: (_, row) => (
        <Button
          size="small"
          type="primary"
          icon={<EditOutlined />}
          disabled={!canManageModuleRollout(row)}
          onClick={() => rolloutEditor.openRolloutCreate(row)}
        >
          创建 rollout
        </Button>
      ),
    },
  ];

  const detailModuleColumns: ColumnsType<UpdateModuleVersionRecord> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '模块 Key', dataIndex: 'module_key', width: 160 },
    { title: '版本号', dataIndex: 'module_version', width: 140 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: UpdateModuleStatus) => renderStatusTag(value, moduleStatusMap),
    },
    {
      title: 'OSS Key',
      dataIndex: 'url',
      ellipsis: true,
      render: (value: string, row) => renderOssKey(value, row.download_url),
    },
    {
      title: '校验串',
      dataIndex: 'sha1',
      width: 260,
      render: (value: string) => <Typography.Text copyable={{ text: value }}>{value}</Typography.Text>,
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="上传构建 Manifest">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Form<ReleaseUploadForm> form={uploadForm} layout="vertical" onFinish={uploadRelease}>
            <Flex gap="middle" wrap>
              <Form.Item<ReleaseUploadForm>
                label="版本名称"
                name="name"
                style={{ flex: '1 1 280px' }}
                rules={[
                  { required: true, message: '请输入版本名称' },
                  { max: 128, message: '版本名称不能超过 128 个字符' },
                ]}
              >
                <Input placeholder="例如：2026-04-20 平台更新" disabled={uploading} />
              </Form.Item>
              <Form.Item<ReleaseUploadForm>
                label="备注"
                name="remark"
                style={{ flex: '1 1 320px' }}
                rules={[{ max: 512, message: '备注不能超过 512 个字符' }]}
              >
                <Input placeholder="可选" disabled={uploading} maxLength={512} showCount />
              </Form.Item>
            </Flex>
            <Form.Item<ReleaseUploadForm>
              label="更新说明"
              name="update_description"
              rules={[{ max: 2048, message: '更新说明不能超过 2048 个字符' }]}
            >
              <Input.TextArea rows={4} placeholder="可选，填写本次 Release 的更新内容" disabled={uploading} maxLength={2048} showCount />
            </Form.Item>

            <Space>
              <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                选择 JSON
              </Button>
              <Button type="primary" htmlType="submit" loading={uploading} disabled={!manifest}>
                上传 Release
              </Button>
              {manifestFileName ? <Typography.Text type="secondary">{manifestFileName}</Typography.Text> : null}
            </Space>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              style={{ display: 'none' }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  handleManifestFile(file);
                }
                event.target.value = '';
              }}
            />
          </Form>

          {manifestParseError ? <Alert showIcon type="error" message="Manifest 解析失败" description={manifestParseError} /> : null}

          {manifest ? (
            <Card size="small" title="Manifest 摘要">
              <Descriptions
                bordered
                size="small"
                column={{ xs: 1, sm: 3 }}
                items={[
                  { key: 'quick_version', label: '协议版本', children: manifest.quick_version },
                  { key: 'build_number', label: '构建号', children: manifest.build_number },
                  { key: 'module_count', label: '模块数量', children: manifest.modules.length },
                  { key: 'install_url', label: '安装包 OSS Key', children: renderOssKey(manifest.install_url) },
                  {
                    key: 'install_sha1',
                    label: '安装包校验串',
                    children: <Typography.Text copyable={{ text: manifest.install_sha1 }}>{manifest.install_sha1}</Typography.Text>,
                  },
                ]}
              />
              <Table<UpdateManifestModule>
                rowKey="key"
                size="small"
                style={{ marginTop: 16 }}
                columns={manifestColumns}
                dataSource={manifest.modules}
                pagination={false}
                scroll={{ x: 1200 }}
              />
            </Card>
          ) : null}
        </Space>
      </Card>

      <Row>
        <Col>
          <Typography.Title level={4}>
            构建版本
            <Button style={{ marginLeft: 24 }} icon={<ReloadOutlined />} onClick={() => loadReleaseList()} loading={listLoading}>
              刷新
            </Button>
          </Typography.Title>
        </Col>
      </Row>

      <div>
        <Form<ReleaseFilterForm>
          form={filterForm}
          layout="inline"
          onFinish={() => loadReleaseList(1, page.size)}
          style={{ marginBottom: 16 }}
        >
          <Form.Item<ReleaseFilterForm> label="名称" name="name">
            <Input allowClear placeholder="按名称搜索" />
          </Form.Item>
          <Form.Item<ReleaseFilterForm> label="构建号" name="build_number">
            <Input allowClear placeholder="精确构建号" />
          </Form.Item>
          <Form.Item<ReleaseFilterForm> label="状态" name="status">
            <Select allowClear style={{ width: 120 }} options={releaseStatusOptions} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                查询
              </Button>
              <Button
                onClick={() => {
                  filterForm.resetFields();
                  loadReleaseList(1, page.size);
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Table<UpdateReleaseRecord>
          rowKey="id"
          loading={listLoading}
          columns={releaseColumns}
          dataSource={records}
          expandable={{
            onExpand: (expanded, row) => {
              if (expanded) {
                loadExpandedReleaseDetail(row);
              }
            },
            expandedRowRender: (row) => (
              <Table<UpdateModuleVersionRecord>
                rowKey="id"
                size="small"
                loading={expandedLoadingIDs[row.id]}
                columns={releaseModuleColumns}
                dataSource={expandedDetails[row.id]?.modules || []}
                pagination={false}
                scroll={{ x: 1420 }}
              />
            ),
          }}
          pagination={{
            current: page.current,
            pageSize: page.size,
            total: page.total,
            showSizeChanger: true,
            onChange: (current, size) => loadReleaseList(current, size),
          }}
          scroll={{ x: 1520 }}
        />
      </div>

      <Drawer
        title={detail?.release.name || 'Release 详情'}
        open={detailOpen}
        width={1100}
        onClose={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
      >
        {detailLoading ? <Typography.Text type="secondary">加载中...</Typography.Text> : null}
        {detail ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Typography.Title level={5}>更新说明</Typography.Title>
              {renderMultilineText(detail.release.update_description)}
            </div>
            <Descriptions
              bordered
              size="small"
              column={2}
              items={[
                { key: 'id', label: 'ID', children: detail.release.id },
                { key: 'status', label: '状态', children: renderStatusTag(detail.release.status, releaseStatusMap) },
                { key: 'quick_version', label: '协议版本', children: detail.release.quick_version },
                { key: 'build_number', label: '构建号', children: detail.release.build_number },
                { key: 'created_at', label: '创建时间', children: detail.release.created_at },
                { key: 'updated_at', label: '更新时间', children: detail.release.updated_at },
                { key: 'remark', label: '备注', children: nullableText(detail.release.remark) },
                {
                  key: 'sha',
                  label: 'Manifest SHA256',
                  children: (
                    <Typography.Text copyable={{ text: detail.release.manifest_sha256 }}>{detail.release.manifest_sha256}</Typography.Text>
                  ),
                },
              ]}
            />
            <Table<UpdateModuleVersionRecord>
              rowKey="id"
              size="small"
              columns={detailModuleColumns}
              dataSource={detail.modules}
              pagination={false}
              scroll={{ x: 1280 }}
            />
            <Input.TextArea readOnly rows={12} value={JSON.stringify(detail.manifest, null, 2)} />
          </Space>
        ) : null}
      </Drawer>

      <Modal
        title="修改 Release"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => editForm.submit()}
        confirmLoading={editLoading}
        destroyOnHidden
      >
        <Form<ReleaseEditForm> form={editForm} layout="vertical" onFinish={submitEdit} preserve={false}>
          <Form.Item<ReleaseEditForm> label="版本名称" name="name" rules={[{ required: true, message: '请输入版本名称' }, { max: 128 }]}>
            <Input />
          </Form.Item>
          <Form.Item<ReleaseEditForm> label="更新说明" name="update_description" rules={[{ max: 2048 }]}>
            <Input.TextArea rows={4} maxLength={2048} showCount />
          </Form.Item>
          <Form.Item<ReleaseEditForm> label="备注" name="remark" rules={[{ max: 512 }]}>
            <Input.TextArea rows={3} maxLength={512} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {rolloutEditor.modal}
    </Space>
  );
}
